import { FC } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, RefreshCw } from "lucide-react";

import { Button } from "@/components/ui/button";

interface AppendixRenderErrorProps {
  /** Left out when a retry has nothing new to try. */
  onRetry?: () => void;
}

/**
 * What one appendix shows in place of its pages when it cannot be displayed.
 * Amber rather than red on purpose: the appendix is still in the contract PDF,
 * so nothing is lost but the inline preview - this is a warning, not a failure
 * the reader has to act on.
 */
const AppendixRenderError: FC<AppendixRenderErrorProps> = ({ onRetry }) => {
  const { t } = useTranslation(["appendices"]);

  return (
    <div className="mx-7 my-4 rounded-2xl border border-amber-200 bg-amber-50/70 p-5">
      <div className="flex items-center gap-4">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-amber-100 to-orange-200 ring-8 ring-amber-50">
          <AlertTriangle className="text-amber-600" size={20} strokeWidth={2.25} />
        </span>

        <div className="flex min-w-0 flex-col items-start gap-1">
          <span className="text-base leading-6 font-bold text-amber-900">
            {t(
              "appendices:render-error-title",
              "This appendix could not be displayed"
            )}
          </span>

          <span className="text-sm leading-5 font-medium text-amber-800">
            {t(
              "appendices:render-error",
              "It is still included in the contract PDF you can download above."
            )}
          </span>

          {onRetry && (
            <Button
              className="mt-3 cursor-pointer border-amber-300 text-amber-900 hover:bg-amber-100"
              size="sm"
              type="button"
              variant="outline"
              onClick={onRetry}
            >
              <RefreshCw className="h-4 w-4 mr-1.5" />
              {t("appendices:render-retry", "Try again")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AppendixRenderError;
